<script setup lang="ts">
import { enUS } from 'date-fns/locale'
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker'

import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { RowInfo, RowSlot } from '@shared/ui/other'
import { ModalDefault } from '@shared/ui/modals'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppTextarea, NativeSelect } from '@shared/ui/fields'
import { BtnIcon, BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import DisciplineTaskActions from '../DisciplineTaskActions.vue'
import DisciplineTutorSelect from '../DisciplineTutorSelect.vue'
import { useModalDisciplineTaskEdit } from '../../model/modal-discipline-task-edit.hook'

defineOptions({
  name: 'ModalDisciplineTaskEdit',
})

const {
  refIsOpen,
  refIsLoading,
  refTask,
  refForm,
  refTutorId,
  refDueDate,
  refTitle,
  refTypeLabel,
  refIsManual,
  refIsProgressReport,
  refIsTaskOpen,
  refHasFilePackSelect,
  refHasReason,
  refCreatedAt,
  refLessonDate,
  refCompletionDate,
  refDateRange,
  refKid,
  refDiscipline,
  refStatusOptions,
  refFilePackOptions,
  refFilePackUrl,
  refIsChanged,
  canManageTutorTasks,
  emptyCell,
  setStatus,
  setFilePack,
  setDescription,
  openFilePack,
  closeModal,
  saveTask,
} = useModalDisciplineTaskEdit()
</script>

<template>
  <ModalDefault
    :show="refIsOpen"
    :loading="refIsLoading"
    :title="refTitle"
    @click:overlay="closeModal"
  >
    <div
      v-if="refTask && refForm"
      class="modal-discipline-task-edit"
    >
      <div
        v-if="refIsTaskOpen"
        class="modal-discipline-task-edit__shortcuts"
      >
        <DisciplineTaskActions
          :task="refTask"
          shortcuts
        />
      </div>
      <RowInfo
        title="Task type:"
        :value="refTypeLabel"
      />
      <RowInfo
        v-if="refIsManual"
        title="Created at:"
        :value="refCreatedAt"
      />
      <RowInfo
        v-else
        :title="refIsProgressReport ? 'Lesson date & time:' : 'Lesson date:'"
        :value="refLessonDate"
      />
      <RowInfo
        title="Completion date:"
        :value="refCompletionDate"
      />
      <RowInfo
        v-if="refIsProgressReport"
        title="Comment from tutor:"
        :value="refTask.comment || emptyCell"
      />
      <RowInfo :title="refIsProgressReport ? 'Kids name:' : 'Kid name:'">
        <LinkTextPrimary
          v-if="refKid.url"
          :href="refKid.url"
        >
          {{ refKid.text }}
        </LinkTextPrimary>
      </RowInfo>
      <RowInfo title="Discipline:">
        <LinkTextPrimary
          v-if="refDiscipline.url"
          :href="refDiscipline.url"
        >
          {{ refDiscipline.text }}
        </LinkTextPrimary>
        <AppText
          v-else
          name="body1"
        >
          {{ refDiscipline.text }}
        </AppText>
      </RowInfo>
      <RowInfo
        title="Booking ID:"
        :value="String(refTask.booking_id)"
      />
      <RowInfo
        v-if="refIsProgressReport"
        title="Date range:"
        :value="refDateRange"
      />
      <DisciplineTutorSelect
        v-model="refTutorId"
        :disabled="!canManageTutorTasks"
      />
      <NativeSelect
        name="task_status"
        label="Status:"
        :options="refStatusOptions"
        :value="refForm.status"
        :disabled="!canManageTutorTasks"
        @change="setStatus"
      />
      <RowSlot
        v-if="!refIsManual"
        label="Due date:"
      >
        <Datepicker
          v-model="refDueDate"
          :locale="enUS"
          :week-start="1"
          :formats="{ input: 'MMM dd, yyyy HH:mm' }"
          :time-config="{ timePickerInline: true }"
          :disabled="!canManageTutorTasks"
          placeholder="Select date"
          :teleport-center="true"
        />
      </RowSlot>
      <template v-if="!refIsProgressReport">
        <div
          v-if="refHasFilePackSelect"
          class="modal-discipline-task-edit__files"
        >
          <NativeSelect
            name="file_pack"
            label="Uploaded files:"
            placeholder-enabled
            placeholder="Select file pack"
            :options="refFilePackOptions"
            :value="refForm.file_pack_id"
            :disabled="!canManageTutorTasks"
            @change="setFilePack"
          />
          <BtnIcon
            :icon="faArrowUpRightFromSquare"
            title="Show uploaded files"
            color="blue"
            :disabled="!refFilePackUrl"
            @click="openFilePack"
          />
        </div>
        <RowInfo
          v-else
          title="Uploaded files:"
          :value="emptyCell"
        />
        <AppTextarea
          v-if="refIsManual"
          name="task_description"
          label="Description:"
          placeholder="Type here"
          :value="refForm.description ?? ''"
          :disabled="!canManageTutorTasks"
          @input="setDescription"
        />
        <RowInfo
          v-else
          title="Description:"
          :value="refTask.description || emptyCell"
        />
        <RowInfo
          v-if="refHasReason"
          title="Reason:"
          :value="refTask.reason || emptyCell"
        />
        <RowInfo
          title="Comment from tutor:"
          :value="refTask.comment || emptyCell"
        />
      </template>
    </div>
    <template #controls>
      <div class="modal-discipline-task-edit__controls">
        <BtnPrimary
          v-if="canManageTutorTasks"
          text="Save"
          color="emerald"
          :disabled="refIsLoading || !refIsChanged"
          @click="saveTask"
        />
        <BtnSecondary
          text="Close"
          color="black"
          :disabled="refIsLoading"
          @click="closeModal"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalDisciplineTaskForm.scss" />
